import { useCallback, useEffect, useState } from "react";
import { Button, Card, Tag, Space, Typography, Empty, Spin, Descriptions, Modal, Input, message, Alert } from "antd";
import { ShareAltOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, FileTextOutlined } from "@ant-design/icons";
import axiosInstance from "../../../../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../../../../variables/constants";
import { rightsService } from "../../../../../services/rightsService";

const { Text, Title } = Typography;

const STATUT_TAG = {
    EN_ATTENTE: { color: "orange", label: "En attente de séance" },
    INSCRIT: { color: "blue", label: "Inscrit à une séance" },
    APPROUVE: { color: "green", label: "Approuvé" },
    REJETE: { color: "red", label: "Rejeté" },
    AJOURNE: { color: "gold", label: "Ajourné" },
    CLOTURE: { color: "default", label: "Clôturé" },
};

const isPool = () => {
    try { return (LOGGED_USER?.profil?.organisme?.typeOrganisme || "").toUpperCase() === "POOL"; }
    catch (e) { return false; }
};
const canPartager = () => isPool() || rightsService("HSI110");
const isMembre = () => isPool() || rightsService("HSI111");

const fmt = (v) => (v || v === 0 ? Number(v).toLocaleString("fr-FR") : "-");

/**
 * Onglet « Commission Technique » du cockpit sinistre. Affiche l'état des dossiers de ce sinistre en
 * commission, permet le partage (Pool + compagnie détentrice) et la décision (membres de la commission).
 */
const CommissionTechniqueTab = ({ sinistre }) => {
    const [loading, setLoading] = useState(false);
    const [dossiers, setDossiers] = useState([]);
    const codeId = sinistre?.codeId;

    const charger = useCallback(() => {
        if (!codeId) return;
        setLoading(true);
        axiosInstance.get(`/api/commission-technique/sinistre/${codeId}`)
            .then((res) => setDossiers(Array.isArray(res.data) ? res.data : []))
            .catch(() => setDossiers([]))
            .finally(() => setLoading(false));
    }, [codeId]);

    useEffect(() => { charger(); }, [charger]);

    const partager = () => {
        Modal.confirm({
            title: "Partager en Commission Technique",
            content: "Le dossier deviendra visible par toutes les compagnies jusqu'à la clôture de la séance. Confirmer ?",
            okText: "Partager", cancelText: "Annuler",
            onOk: () => axiosInstance.post(`/api/commission-technique/partager/${codeId}`)
                .then(() => { message.success("Dossier partagé en Commission Technique."); charger(); })
                .catch((e) => message.error(e?.response?.data?.message || "Échec du partage.")),
        });
    };

    const statuer = (dossierCodeId, decision) => {
        let avis = "";
        Modal.confirm({
            title: `Décision : ${STATUT_TAG[decision]?.label || decision}`,
            content: (
                <Input.TextArea rows={3} placeholder="Avis / motivation (optionnel)"
                    onChange={(e) => { avis = e.target.value; }} />
            ),
            okText: "Valider la décision", cancelText: "Annuler",
            onOk: () => axiosInstance.put(`/api/commission-technique/dossiers/${dossierCodeId}/statuer`, { decision, avis })
                .then(() => { message.success("Décision enregistrée."); charger(); })
                .catch((e) => message.error(e?.response?.data?.message || "Échec de la décision.")),
        });
    };

    const actif = dossiers.find((d) => d.statut !== "CLOTURE");

    return (
        <Spin spinning={loading}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <Title level={5} style={{ margin: 0 }}>Commission Technique Sinistre</Title>
                {canPartager() && !actif && (
                    <Button type="primary" icon={<ShareAltOutlined />} onClick={partager}>
                        Partager en commission
                    </Button>
                )}
            </div>

            {dossiers.length === 0 && <Empty description="Ce dossier n'est pas passé en Commission Technique." />}

            <Space direction="vertical" style={{ width: "100%" }} size={12}>
                {dossiers.map((d) => {
                    const tg = STATUT_TAG[d.statut] || { color: "default", label: d.statut };
                    return (
                        <Card key={d.codeId} size="small" bordered
                            title={<Space><Tag color={tg.color}>{tg.label}</Tag>
                                <Text type="secondary">{d.motif === "AUTO_SEUIL" ? "Passage automatique (seuil)" : "Partage manuel"}</Text></Space>}>
                            <Descriptions size="small" column={2} labelStyle={{ color: "#888" }}>
                                <Descriptions.Item label="Branche">{d.brancheCode || "-"}</Descriptions.Item>
                                <Descriptions.Item label="Compagnie d'origine">{d.organismeOrigineNom || d.organismeOrigineCode || "-"}</Descriptions.Item>
                                {d.motif === "AUTO_SEUIL" && (
                                    <Descriptions.Item label="Règlement déclencheur">{fmt(d.montantDeclencheur)} F</Descriptions.Item>
                                )}
                                <Descriptions.Item label="Évaluation">{fmt(d.evaluation)} F</Descriptions.Item>
                                {d.avis && <Descriptions.Item label="Avis" span={2}>{d.avis}</Descriptions.Item>}
                            </Descriptions>

                            {d.motif === "AUTO_SEUIL" && d.statut !== "APPROUVE" && (
                                <Alert style={{ marginTop: 8 }} type="warning" showIcon
                                    message="Règlement bloqué : la validation ne sera possible qu'après décision favorable de la commission." />
                            )}

                            {isMembre() && d.statut !== "CLOTURE" && d.statut !== "APPROUVE" && (
                                <Space style={{ marginTop: 12 }}>
                                    <Button size="small" type="primary" icon={<CheckCircleOutlined />}
                                        onClick={() => statuer(d.codeId, "APPROUVE")}>Approuver</Button>
                                    <Button size="small" danger icon={<CloseCircleOutlined />}
                                        onClick={() => statuer(d.codeId, "REJETE")}>Rejeter</Button>
                                    <Button size="small" icon={<ClockCircleOutlined />}
                                        onClick={() => statuer(d.codeId, "AJOURNE")}>Ajourner</Button>
                                </Space>
                            )}

                            {d.seance?.comptesRendus?.length > 0 && (
                                <div style={{ marginTop: 10 }}>
                                    <Text type="secondary"><FileTextOutlined /> Comptes rendus : </Text>
                                    {d.seance.comptesRendus.map((cr, i) => <Tag key={i}>{cr.split("/").pop()}</Tag>)}
                                </div>
                            )}
                        </Card>
                    );
                })}
            </Space>
        </Spin>
    );
};

export default CommissionTechniqueTab;
